import { defineStore } from 'pinia'

export const useCartStore = defineStore('cart', () => {
  // API endpoint'lerini al
  const { API_ENDPOINTS } = useApiEndpoints()
  const toast = useCustomToast()
  const uiStore = useUiStore()
  const buyBoxStore = useProductBuyboxStore()
  const router = useRouter()
  /**
   * State
   */
  const state = {
    cartItems: ref([]),
    cartId: ref(null),
    cartTotal: ref(0),
    totalQuantity: ref(0),
    loading: ref(false),
    error: ref(null),
    shipping_fee: ref(0),
    isCartOpen: ref(false),
    lastAddedItemId: ref(null)
  }

  /**
   * Getters
   */
  const getters = {
    totalItems: computed(() => state.totalQuantity.value),
    subtotal: computed(() => state.cartTotal.value + state.shipping_fee.value),
    hasOutOfStockItems: computed(() => {
      return state.cartItems.value.some(item => item.is_in_stock_enough === false)
    }),
    hasPriceChanges: computed(() => {
      return state.cartItems.value.some(item => 
        item.original_price !== item.winner_data?.sale_price
      )
    }),
    priceChangedItemsCount: computed(() => {
      return state.cartItems.value.filter(item => 
        item.original_price !== item.winner_data?.sale_price
      ).length
    }),
    outOfStockItemsCount: computed(() => {
      return state.cartItems.value.filter(item => 
        item.is_in_stock_enough === false
      ).length
    }),
    groupedByVendor: computed(() => {
      return state.cartItems.value.reduce((groups, item) => {
        // Vendor bilgilerini al
        const vendorId = item.winner_data?.vendor?.id || null
        const vendorName =
          item.winner_data?.vendor?.name || 'Ürünün satıcısı yok.'
        const vendorSlug = item.winner_data?.vendor?.slug || vendorId

        // Vendor grubu yoksa oluştur
        if (!groups[vendorId]) {
          groups[vendorId] = {
            vendor_id: vendorId,
            vendor_name: vendorName,
            vendor_slug: vendorSlug,
            items: [],
            total: 0
          }
        }

        groups[vendorId].items.push(item)

        return groups
      }, {})
    })
  }

  /**
   * UI Actions
   */
  const uiActions = {
    openCartSlideOver: () => {
      state.isCartOpen.value = true
    },

    closeCartSlideOver: () => {
      state.isCartOpen.value = false
    },

    highlightLastAddedItem: (itemId) => {
      state.lastAddedItemId.value = itemId
      setTimeout(() => {
        state.lastAddedItemId.value = null
      }, 3000)
    }
  }

  /**
   * Cart Actions
   */
  const cartActions = {
    handleAddToCart: async (payload, isBasic = false) => {

      const queries = router.currentRoute.value.query

      state.loading.value = true
      try {
        const response = await useBaseOFetchWithAuth(
          isBasic
            ? API_ENDPOINTS.anonymous.cart.create.forBasic
            : API_ENDPOINTS.anonymous.cart.create.forVariant,
          {
            method: 'POST',
            body: payload,
            query: queries
          }
        )

        if (response.success) {
          await cartActions.fetchCartItems()
          uiActions.highlightLastAddedItem(response.cart_item_id)
          uiStore.cartSlide = true
        }

        if (response?.price_changes?.has_changes) {
          toast.warning(response.price_changes.message)
        }

        return response
      } catch (error) {
        if (error?.response?._data?.data?.stock_insufficient) {
          buyBoxStore.fetchBuyboxData(router.currentRoute.value.path)
        }
      } finally {
        state.loading.value = false
      }
    },

    addToCartForVariant: async ({
      productId,
      productVariationId,
      quantity,
      originalVendorId,
      originalPrice,
      cartItemId
    }) => {
      return cartActions.handleAddToCart({
        product_id: productId,
        variation_id: productVariationId,
        quantity,
        original_vendor_id: originalVendorId,
        original_price: originalPrice,
        cart_item_id: cartItemId
      })
    },

    addToCartForBasic: async ({
      productId,
      quantity,
      originalVendorId,
      originalPrice,
      cartItemId
    }) => {
      console.log(productId, quantity, originalVendorId, originalPrice, cartItemId)

      return cartActions.handleAddToCart(
        {
          product_id: productId,
          quantity,
          original_vendor_id: originalVendorId,
          original_price: originalPrice,
          cart_item_id: cartItemId
        },
        true
      )
    },

    fetchCartItems: async () => {
      state.loading.value = true
      try {
        const response = await useBaseOFetchWithAuth(
          API_ENDPOINTS.anonymous.cart.show
        )
        const data = response?.data

        state.cartId.value = data?.id ?? null
        state.cartItems.value = Array.isArray(data?.cart_items) ? data.cart_items : []
        state.cartTotal.value = Number(data?.cart_total) || 0
        state.totalQuantity.value = Number(data?.total_quantity) || 0
        state.shipping_fee.value = Number(data?.shipping_fee) || 0
      } finally {
        state.loading.value = false
      }
    },

    removeFromCart: async (cartItemId) => {
      state.loading.value = true
      try {
        await useBaseOFetchWithAuth(
          API_ENDPOINTS.anonymous.cart.delete(cartItemId),
          {
            method: 'DELETE'
          }
        )

        await cartActions.fetchCartItems()
      } finally {
        state.loading.value = false
      }
    },

    updateCartItemQuantity: async (cartItem, newQuantity) => {
      if (newQuantity < 1) {
        return cartActions.removeFromCart(cartItem.id)
      }

      state.loading.value = true
      try {
        await useBaseOFetchWithAuth(
          API_ENDPOINTS.anonymous.cart.update(cartItem.id),
          {
            method: 'PUT',
            body: {
              quantity: newQuantity
            }
          }
        )

        await cartActions.fetchCartItems()
      } finally {
        state.loading.value = false
      }
    },

    clearCart: () => {
      state.cartItems.value = []
      state.cartId.value = null
      state.cartTotal.value = 0
      state.totalQuantity.value = 0
      state.shipping_fee.value = 0
    }
  }

  return {
    // State (refs'i doğrudan döndür)
    ...state,

    // Getters
    ...getters,

    // Actions
    ...uiActions,
    ...cartActions
  }
})
